import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Avatar,
  Divider,
  Chip,
  IconButton,
} from '@mui/material';
import {
  CheckCircle,
  Error,
  NetworkCheck,
  NetworkPing,
  Close,
  AccessTime,
  Router,
} from '@mui/icons-material';

interface TestResultDialogProps {
  open: boolean;
  onClose: () => void;
  result: {
    type: 'connection' | 'ping';
    success: boolean;
    message: string;
    routerName: string;
    routerIp: string;
    responseTime?: number;
    timestamp: Date;
  } | null;
}

const TestResultDialog: React.FC<TestResultDialogProps> = ({ open, onClose, result }) => {
  if (!result) return null;

  const getResultIcon = () => {
    if (result.success) {
      return (
        <Avatar sx={{ bgcolor: 'success.main', width: 64, height: 64 }}>
          <CheckCircle sx={{ fontSize: 32 }} />
        </Avatar>
      );
    } else {
      return (
        <Avatar sx={{ bgcolor: 'error.main', width: 64, height: 64 }}>
          <Error sx={{ fontSize: 32 }} />
        </Avatar>
      );
    }
  };

  const getTypeIcon = () => {
    return result.type === 'connection' ? <NetworkCheck /> : <NetworkPing />;
  };

  const getTitle = () => {
    const action = result.type === 'connection' ? 'Test Koneksi' : 'Ping Router';
    return `${action} ${result.success ? 'Berhasil' : 'Gagal'}`;
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('id-ID', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'visible',
        }
      }}
    >
      <DialogTitle sx={{ position: 'relative', textAlign: 'center', pb: 1 }}>
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'grey.500',
          }}
        >
          <Close />
        </IconButton>
        
        <Box display="flex" flexDirection="column" alignItems="center" gap={2} pt={2}>
          {getResultIcon()}
          <Typography variant="h5" component="h2" fontWeight="bold">
            {getTitle()}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        <Box display="flex" flexDirection="column" gap={3}>
          {/* Router Info */}
          <Box>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Router color="primary" />
              <Typography variant="h6" color="primary">
                Informasi Router
              </Typography>
            </Box>
            <Box 
              sx={{ 
                bgcolor: 'grey.50', 
                p: 2, 
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.200'
              }}
            >
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2" color="text.secondary">
                  Nama Router:
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {result.routerName}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  IP Address:
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {result.routerIp}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Divider />

          {/* Test Details */}
          <Box>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              {getTypeIcon()}
              <Typography variant="h6" color="primary">
                Detail Test
              </Typography>
            </Box>
            
            <Box display="flex" flexDirection="column" gap={2}>
              <Box display="flex" alignItems="center" gap={2}>
                <Chip
                  icon={getTypeIcon()}
                  label={result.type === 'connection' ? 'Test Koneksi' : 'Ping Test'}
                  color="primary"
                  variant="outlined"
                />
                <Chip
                  label={result.success ? 'Berhasil' : 'Gagal'}
                  color={result.success ? 'success' : 'error'}
                />
              </Box>

              {result.responseTime && (
                <Box display="flex" alignItems="center" gap={1}>
                  <AccessTime sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    Response Time:
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {result.responseTime}ms
                  </Typography>
                </Box>
              )}

              <Box display="flex" alignItems="center" gap={1}>
                <AccessTime sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  Waktu Test:
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {formatTime(result.timestamp)}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Divider />

          {/* Result Message */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" mb={1}>
              Pesan:
            </Typography>
            <Box
              sx={{
                bgcolor: result.success ? 'success.50' : 'error.50',
                border: '1px solid',
                borderColor: result.success ? 'success.200' : 'error.200',
                borderRadius: 2,
                p: 2,
              }}
            >
              <Typography 
                variant="body2" 
                color={result.success ? 'success.800' : 'error.800'}
                sx={{ whiteSpace: 'pre-wrap' }}
              >
                {result.message}
              </Typography>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button 
          variant="contained" 
          onClick={onClose}
          fullWidth
          size="large"
          sx={{ borderRadius: 2 }}
        >
          Tutup
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TestResultDialog;
